import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel
} from '@chakra-ui/form-control'
import {
  Modal,
  ModalBody, ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Text as ChakraText,
  VStack
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { addLeadingSlash, addTrailingSlash } from '../../../utils/urlHelpers'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'
import PasswordInput from '../PasswordInput'

interface Props {
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  password: string
}

const PublicLinkPasswordModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { urlPrefix } = useContext(PublicLinksInfoContext)
  const endpoint = `${urlPrefix}api/auth`
  const [formError, setFormError] = useState<string>('')

  const { post, request, response, data, loading } = useFetch<{
    message: string
    help: string
  } | {}>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={true} size="xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ password: '' }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
              const postResponse = await request.post(
                endpoint,
                { password: values.password },
              )
              if (response.status === 200) {
                window.location.reload()
              } else {
                if (response.status === 403) {
                  setFormError(
                    (postResponse as {
                      message: string
                      help: string
                    })?.message ??
                      t(
                        'invalid_account_credentials_entered',
                        'You have entered invalid account credentials.',
                      ),
                  )
                } else {
                  if (response.status === 404) {
                    setFormError(
                      (postResponse as {
                        message: string
                        help: string
                      })?.message ??
                        t(
                          'resource_not_found',
                          'Resource not found',
                        ),
                    )
                  } else {
                    setFormError(
                      t("something_went_wrong", "Something went wrong. Please try again.")
                    )
                  }
                }
              }
          }}
          validationSchema={Yup.object().shape({
            password: Yup.string().required('Required').max(100, "Password cannot be longer than 100 characters"),
          })}
        >
          {({ isSubmitting, submitForm, isValid, dirty }) => (
            <>
              <ModalHeader>
                {t('enter_password', 'Enter password')}
              </ModalHeader>
              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <VStack spacing={4} alignItems="flex-start">
                  <ChakraText>
                    {t(
                      'public_link_password_prompt',
                      'This resource requires a password to access. This password is different from your ImageKit account password.',
                    )}
                  </ChakraText>
                  <Field name="password">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.password && form.touched.password,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('password', 'Password')}</FormLabel>
                        <PasswordInput
                          field={field}
                          isDisabled={false}
                          isPasswordSetByUser={true}
                          showToggle={true}
                          onKeyPress={e => {
                            if (e.key === 'Enter' && field.value !== '') {
                              void submitForm()
                            }
                          }}
                        />
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  type="submit"
                  isDisabled={!isValid || !dirty || isSubmitting || loading || response.status === 200}
                  isLoading={isSubmitting || loading || response.status === 200}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default PublicLinkPasswordModal
